import {replace} from 'lodash';
import {Fragment, PureComponent} from 'react';
import {WithTranslation, withTranslation} from 'react-i18next';
import {Router, Switch, Redirect} from 'react-router-dom';
import {SocketLikeWithNamespace} from 'socket.io-react-hook/lib/cjs/types';

import {firebase} from './config/firebase';
import {rolesApi, userApi} from 'api';
import {
    OnlineSessionsLoader, OnlineSessionsNotifier, AppNotifier, SystemDateValidator, UserSocketConnection,
    UserFavicon, UserPasswordChangeReminder, NewConnectionModal, notifier, AppointmentReminder, ErrorBoundary
} from 'components';
import {DEFAULT_LANGUAGE} from 'constants/language';
import {UserContext} from 'context';
import {history} from 'context/history';
import {AccountStateName, UserPasswordExpireStatus} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {getPublicRoutes, userRoutes} from 'pages';
import {ErrorPage} from 'pages/Common';
import {authDataStore, MediaDevicesStorage, mediaDevicesStore, socketStore, Subscription} from 'services';
import {LanguageService} from 'services/Language';
import {UserDataStorage} from 'services/UserData';
import {UserContextProps} from 'types';
import {apiUtils, generateUtils, logger, RedirectToPrivateRoute, RETURN_URL_QUERY, userAuthUtils} from 'utils';

import {sessionApi} from './api/session';

const LOGIN_PAGE_KEYS = TRANSLATION_KEYS.LOGIN_PAGE;
const COMMON_MESSAGES_KEYS = TRANSLATION_KEYS.COMMON.MESSAGES;

type State = {
    contextProps: UserContextProps | null;
    socket: SocketLikeWithNamespace;
    socketInitialAuthenticated: boolean;
    loadSettingsError: boolean;
    loadProfileError: boolean;
    profileLoaded: boolean;
    authCheckCompleted: boolean;
    loadPermissionError: boolean;
    permissionsLoaded: boolean;
    returnUrl: string;
}

const initialState: State = {
    contextProps: null,
    socket: null,
    socketInitialAuthenticated: false,
    loadSettingsError: false,
    authCheckCompleted: false,
    permissionsLoaded: false,
    loadProfileError: false,
    profileLoaded: false,
    loadPermissionError: false,
    returnUrl: null
};

const SESSION_REFRESH_TOKEN_QUERY = 'srt';
const EXTERNAL_AUTH_SOURCE_QUERY = 'source';
const EXTERNAL_AUTH_TOKEN_QUERY = 'token';
const EXTERNAL_AUTH_URL = '/authorize';

type Props = WithTranslation;

class UserApp extends PureComponent<Props, State> {

    readonly state = initialState;

    sessionAccessOnly: boolean;
    subscriptions: Subscription[];

    get userDataLoaded() {
        const {contextProps, loadSettingsError, loadProfileError, profileLoaded, loadPermissionError, permissionsLoaded} = this.state;
        return contextProps?.socket && contextProps?.auth && !loadSettingsError && profileLoaded && !loadProfileError &&
            !loadPermissionError && permissionsLoaded;
    }

    get appReady() {
        return this.state.authCheckCompleted && (!userAuthUtils.isLoggedIn() || this.userDataLoaded);
    }

    async componentDidMount() {
        this.setUserSessionId();
        const lang = LanguageService.getLanguage();
        if (!lang || !LanguageService.isValidLanguage(lang)) {
            LanguageService.setLanguage(DEFAULT_LANGUAGE);
        } else {
            LanguageService.setLanguage(lang);
        }
        await this.identify();

        if (userAuthUtils.isLoggedIn()) {
            await this.onSuccessLogin();
        }

        this.subscriptions = [
            authDataStore.subscribe((value, prevValue) => {
                if (this.userDataLoaded && prevValue?.accessToken && !value?.accessToken) {
                    this.setState({...initialState, authCheckCompleted: true});
                }
            }),
            socketStore.subscribe((value) => {
                if (!this.state.socketInitialAuthenticated && value.authenticated) {
                    this.setState({socketInitialAuthenticated: true});
                }
            }),
            mediaDevicesStore.subscribe((value) => MediaDevicesStorage.setItem(value))
        ];
    }

    componentDidUpdate() {
        if (this.appReady && this.state.returnUrl) {
            this.setState({returnUrl: null});
        }
    }

    componentWillUnmount() {
        this.subscriptions?.forEach((unsubscribe) => unsubscribe());
    }

    setUserSessionId() {
        const data = UserDataStorage.getItem();
        if (!data?.sessionId) {
            UserDataStorage.setItem({...data, sessionId: generateUtils.generateToken()});
        }
    }

    async setFirebase(): Promise<void> {
        if (await firebase.isSupported()) {
            if (Notification.permission !== 'denied') {
                firebase.initialize();
                firebase.getToken().then((currentToken) => {
                    if (currentToken) {
                        userApi.updateNotificationToken(currentToken);
                    } else {
                        return Notification.requestPermission();
                    }
                }).catch((error) => {
                    logger.error({category: logger.category.Notification, message: 'An error occurred while retrieving token'}, error);
                });
            } else {
                logger.warn({
                    category: logger.category.Notification,
                    message: 'This browser will not receive browser notification due to denied in settings'
                });
            }
        } else {
            logger.warn({category: logger.category.Notification, message: 'This browser does not support browser notification'});
        }
    }

    identify = async () => {
        await this.checkExternalAuth();
        if (!userAuthUtils.isLoggedIn()) {
            try {
                await this.refreshAccessToken();
                // eslint-disable-next-line no-empty
            } catch {}
        } else {
            this.setState({authCheckCompleted: true});
        }
    };

    checkExternalAuth = async () => {
        await this.checkSessionOnlyToken();
        await this.checkExternalAuthToken();
    };

    checkSessionOnlyToken = async () => {
        const searchParams = new URLSearchParams(history.location.search);
        const refreshSessionToken = searchParams.get(SESSION_REFRESH_TOKEN_QUERY);
        if (refreshSessionToken) {
            try {
                const {accessToken} = await sessionApi.getSessionAccessToken({token: refreshSessionToken});
                userAuthUtils.setAccessToken(accessToken);
                this.sessionAccessOnly = true;
            // eslint-disable-next-line no-empty
            } catch (error: any) {
                const data = apiUtils.getValidationErrors(error);
                if (data?.errors?.ip) {
                    notifier.error({
                        title: this.props.t(LOGIN_PAGE_KEYS.ERRORS.IP_WHITELIST),
                        ...(data.errors.ip.value && {details: data.errors.ip.value})
                    });
                }
            }
        }
    };

    checkExternalAuthToken = async () => {
        const isAuthLocation = history.location.pathname === EXTERNAL_AUTH_URL;
        if (isAuthLocation) {
            const searchParams = new URLSearchParams(history.location.search);
            const integrationSource = searchParams.get(EXTERNAL_AUTH_SOURCE_QUERY);
            const oneTimeToken = searchParams.get(EXTERNAL_AUTH_TOKEN_QUERY);
            if (integrationSource && oneTimeToken) {
                try {
                    const {accessToken} = await userApi.loginByAuthToken(integrationSource, oneTimeToken);
                    userAuthUtils.setAccessToken(accessToken);
                    const returnUrl = searchParams.get(RETURN_URL_QUERY);
                    const {origin} = window.location;
                    if (returnUrl) {
                        this.setState({returnUrl: replace(returnUrl, origin, '')});
                    }
                    // eslint-disable-next-line no-empty
                } catch {}
            }
        }
    };

    setContext = async (): Promise<UserContextProps | null> => {
        const {sessionAccessOnly} = this;
        if (userAuthUtils.isLoggedIn()) {
            try {
                const settings = await userApi.getSettings();
                LanguageService.setLanguage(settings.common.language);
                const contextProps = {
                    ...this.state.contextProps,
                    auth: {sessionAccessOnly},
                    settings,
                    reloadSettings: this.setContext,
                };

                this.setState({contextProps});
                return contextProps;
            } catch {
                userAuthUtils.resetLocalData();
                this.setState({loadSettingsError: true});
                return null;
            }
        }
    };

    onChangeSocket = (socket: SocketLikeWithNamespace) => {
        this.setState(({contextProps}) => ({contextProps: {...(contextProps && contextProps), socket}}));
    };

    async setProfile() {
        try {
            await userAuthUtils.fetchCurrentUser();
            this.setState({profileLoaded: true});
        } catch {
            userAuthUtils.resetLocalData();
            this.setState({loadProfileError: true});
        }
    }

    async setPermissions() {
        if (!userAuthUtils.isLoggedIn()) {
            this.setState({loadPermissionError: true});
            return;
        }

        try {
            const permissions = await rolesApi.getUserPermissions();
            userAuthUtils.setUserPermissions(permissions, true);
            this.setState({permissionsLoaded: true});
        } catch {
            this.setState({loadPermissionError: true});
        }
    }

    async refreshAccessToken() {
        try {
            await userAuthUtils.refreshAccessToken();
        } finally {
            this.setState({authCheckCompleted: true});
        }
    }

    onSuccessLogin = async () => {
        await this.setProfile();
        const profile = userAuthUtils.getLocalUser();

        const contextProps = await this.setContext();
        const userPasswordExpireStatus = userAuthUtils.getUserPasswordExpireStatus(
            contextProps?.settings?.registration?.userPasswordExpiration,
            contextProps?.settings?.common?.timeZone
        );

        if (userPasswordExpireStatus === UserPasswordExpireStatus.Expired) {
            userAuthUtils.setUserPermissions({passwordExpired: {enabled: true}});
            this.setState({permissionsLoaded: true});
        } else if (profile.accountStateName === AccountStateName.Blocked) {
            userAuthUtils.setUserPermissions({accountBlocked: {enabled: true}});
            this.setState({permissionsLoaded: true});
        } else if (profile.accountStateName === AccountStateName.Unbilled) {
            userAuthUtils.setUserPermissions({accountActivation: {enabled: true}});
            this.setState({permissionsLoaded: true});
        } else {
            await this.setPermissions();
        }

        if (userAuthUtils.isFirebaseSupportedAuthSource() &&
            !this.sessionAccessOnly &&
            profile.accountStateName === AccountStateName.Active
        ) {
            this.setFirebase();
        }
    };

    render() {
        const {onSuccessLogin, appReady, userDataLoaded, onChangeSocket} = this;
        const {contextProps, returnUrl, authCheckCompleted, socketInitialAuthenticated} = this.state;
        const {t} = this.props;

        return (
            <UserContext.Provider value={contextProps}>
                <Router history={history}>
                    {appReady &&
                        <Fragment>
                            <ErrorBoundary
                                fallback={
                                    <ErrorPage message={t(COMMON_MESSAGES_KEYS.ERROR.UNEXPECTED)}>
                                        <div className='tw-text-center tw-ts-h5'>
                                            {t(COMMON_MESSAGES_KEYS.CONNECTION.REFRESH_REQUIRED)}
                                        </div>
                                    </ErrorPage>
                                }
                            >
                                <Switch>
                                    {returnUrl && <Redirect to={returnUrl} />}
                                    {getPublicRoutes({onSuccessLogin})}
                                    {userRoutes}
                                    <RedirectToPrivateRoute routes={[]} />
                                </Switch>
                            </ErrorBoundary>
                            {userDataLoaded && socketInitialAuthenticated &&
                                <Fragment>
                                    <OnlineSessionsLoader />
                                    <NewConnectionModal />
                                    <AppointmentReminder />
                                </Fragment>}
                        </Fragment>}
                    {authCheckCompleted &&
                        <Fragment>
                            <AppNotifier />
                            {userAuthUtils.isLoggedIn() &&
                                <Fragment>
                                    {contextProps?.settings && <OnlineSessionsNotifier />}
                                    <UserSocketConnection onChange={onChangeSocket} />
                                    {contextProps?.settings && <UserPasswordChangeReminder />}
                                </Fragment>}
                        </Fragment>}
                    <UserFavicon />
                </Router>
                {contextProps?.settings?.common.systemDateValidation?.enabled && <SystemDateValidator />}
            </UserContext.Provider>
        );
    }
}

export default withTranslation()(UserApp);